import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  User as UserIcon,
  MessageCircle,
  Scroll,
  Shield,
  Sparkles,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import SentimentChip from "../components/SentimentChip";

export default function Profile() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [points, setPoints] = useState({ points: 0, events: [] });
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [tRes, pRes] = await Promise.all([
          api.get("/me/threads"),
          api.get("/me/points"),
        ]);
        setThreads(tRes.data);
        setPoints(pRes.data);
      } catch (e) { setError(formatApiError(e)); }
    })();
  }, []);

  if (!user || user === false) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-14" data-testid="profile-page">
      <div className="animate-fade-up flex items-center gap-6">
        <div className="w-20 h-20 rounded-full flex items-center justify-center font-serif-display text-4xl bg-[#D96C4A] text-white shadow-md">
          {(user.name || "?").slice(0, 1).toUpperCase()}
        </div>
        <div>
          <div className="text-xs tracking-widest uppercase text-[#8A948C]">
            Member since {new Date(user.created_at).toLocaleDateString()}
          </div>
          <h1 className="font-serif-display text-4xl font-medium text-[#2D3B31]" data-testid="profile-name">{user.name}</h1>
          <div className="text-[#5C6B5F] text-sm mt-1">{user.email}</div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {user.role === "admin" && (
              <div className="inline-flex items-center gap-1.5 text-xs text-[#C05B3B] bg-[#F2D8CF] px-3 py-1 rounded-full" data-testid="profile-admin-badge">
                <Shield size={12} /> Moderator
              </div>
            )}
            <div className="inline-flex items-center gap-1.5 text-xs text-[#C05B3B] bg-[#F2D8CF] px-3 py-1 rounded-full" data-testid="profile-points-chip">
              <Sparkles size={12} /> {points.points} JSK Blessing Points
            </div>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-6 mt-12">
        <Stat icon={<Scroll size={20} color="#D96C4A" />} label="Your threads" value={threads.length} testid="stat-threads" />
        <Stat icon={<MessageCircle size={20} color="#D96C4A" />} label="Replies received" value={threads.reduce((a, t) => a + (t.reply_count || 0), 0)} testid="stat-replies" />
        <Stat icon={<UserIcon size={20} color="#D96C4A" />} label="Upvotes received" value={threads.reduce((a, t) => a + (t.upvotes || 0), 0)} testid="stat-upvotes" />
      </div>

      {/* JSK Points activity log */}
      <div className="mt-14">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h2 className="font-serif-display text-2xl text-[#2D3B31] inline-flex items-center gap-2">
            <Sparkles size={20} color="#D96C4A" /> Blessing activity
          </h2>
          <div className="text-sm text-[#5C6B5F]">
            +100 for compassion, kindness, gratitude · −50 for hostility
          </div>
        </div>
        <div className="mt-5 organic-card overflow-hidden" data-testid="points-activity-log">
          {points.events.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#8A948C]">
              No activity yet. Post a thoughtful reflection to begin your journey.
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "#EAE5D9" }}>
              {points.events.map((e) => (
                <li key={e.id} className="px-6 py-4 flex items-start gap-4" data-testid={`points-event-${e.id}`}>
                  <div className={`shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center ${
                    e.delta > 0 ? "bg-[#E6F1E0] text-[#3F6B3A]" :
                    e.delta < 0 ? "bg-[#F2D8CF] text-[#C05B3B]" :
                    "bg-[#F3EFE9] text-[#5C6B5F]"
                  }`}>
                    {e.delta > 0 ? <TrendingUp size={16} /> : e.delta < 0 ? <TrendingDown size={16} /> : <Sparkles size={16} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-[#2D3B31] font-medium">
                        {e.delta > 0 ? `+${e.delta}` : e.delta} JSK
                      </span>
                      <SentimentChip sentiment={e.sentiment} size="xs" />
                      <span className="text-xs text-[#8A948C]">
                        on {e.source} · {e.category}
                      </span>
                    </div>
                    {e.rationale && <p className="text-xs text-[#5C6B5F] mt-1 italic">"{e.rationale}"</p>}
                    <div className="text-xs text-[#8A948C] mt-1">{new Date(e.created_at).toLocaleString()}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-14">
        <h2 className="font-serif-display text-2xl text-[#2D3B31]">Your reflections</h2>
        {error && <div className="mt-4 text-sm text-[#C05B3B]">{error}</div>}
        <div className="mt-6 space-y-4" data-testid="profile-threads-list">
          {threads.length === 0 ? (
            <div className="organic-card p-10 text-center">
              <p className="text-[#5C6B5F]">You haven't started any threads yet.</p>
              <Link to="/chapters" className="btn-accent mt-5 inline-block">Browse chapters</Link>
            </div>
          ) : (
            threads.map((t) => (
              <Link
                key={t.id}
                to={`/threads/${t.id}`}
                className="organic-card p-6 flex items-start justify-between gap-4 block"
                data-testid={`profile-thread-${t.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#8A948C] flex items-center gap-2 flex-wrap">
                    <span>Chapter {t.chapter_number}</span>
                    {t.sentiment && <SentimentChip sentiment={t.sentiment} size="xs" />}
                  </div>
                  <div className="font-serif-display text-xl text-[#2D3B31] mt-1">{t.title}</div>
                  <p className="text-sm text-[#5C6B5F] mt-2 line-clamp-2">{t.body}</p>
                </div>
                <div className="text-right text-xs text-[#8A948C] whitespace-nowrap">
                  <div>{t.upvotes} upvotes</div>
                  <div className="mt-1">{t.reply_count} replies</div>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, testid }) {
  return (
    <div className="organic-card p-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs tracking-widest uppercase text-[#8A948C]">
        {icon} {label}
      </div>
      <div className="font-serif-display text-3xl text-[#2D3B31] mt-3">{value}</div>
    </div>
  );
}
